import { useRef } from 'react'
import { useForm, useFormState } from 'react-final-form'
import { OptimizeMessageConfiguration } from '@phrasee/phrasee-typings/typings/futurama/content_creation'
import { useFlags } from 'launchdarkly-react-client-sdk'
import isEmpty from 'lodash/isEmpty'
import moment from 'moment'
import { SplitSizeData } from 'workflow/interface'
import { fetchCampaignData } from 'workflow/Workflow.actions'

import Button from 'common/components/button'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { isComponentElement } from 'features/unifiedFlow/api/interfaces'
import useGenerateCustomVariantsMutation from 'features/unifiedFlow/api/mutations/useGenerateCustomVariantsMutation'
import useUpdateElementMutation from 'features/unifiedFlow/api/mutations/useUpdateElementMutation'
import { getFormValue } from 'features/unifiedFlow/components/templateSelection/helpers'
import {
  useContent,
  useSelectedElement,
} from 'features/unifiedFlow/contentPage/hooks'
import useWebsocketMessage from 'features/unifiedFlow/contentPage/hooks/websocketMessage'
import { getWsTopic } from 'features/unifiedFlow/contentPage/hooks/websocketMessage/helpers'

import ActionsContainer from './ActionsContainer'

const { REACT_APP_DEFAULT_DATE_FORMAT } = process.env

type Props = {
  isDisabled: boolean
  optimizationSettingsConfiguration: OptimizeMessageConfiguration
  splitCalculationResult?: SplitSizeData
  onReset: () => void
}

const ExperimentSettingsFooter = ({
  isDisabled,
  splitCalculationResult,
  optimizationSettingsConfiguration,
  onReset,
}: Props) => {
  const dispatch = useAppDispatch()
  const form = useForm()
  const formState = useFormState()
  const initialSplitNumberRef = useRef<number | undefined>(
    splitCalculationResult?.split_number
  )

  const { sendWebsocketMessage } = useWebsocketMessage()

  const { content, refetch: refetchContent } = useContent()
  const { data: selectedElement } = useSelectedElement()

  const accountId = useAppSelector((state) => state.authStates.accountId)
  const selectedTopics = useAppSelector(
    (state) => state.unifiedFlow.selectedTopics
  )

  const updateElementMutation = useUpdateElementMutation()
  const generateCustomVariantsMutation = useGenerateCustomVariantsMutation()
  const flags = useFlags()

  const getConfiguration = () => {
    const ownSubjectLine = getFormValue({
      formState,
      name: 'controlSubjectLine',
    })
    const campaignName = getFormValue({
      formState,
      name: 'experimentName',
    })
    const sendDate = getFormValue({
      formState,
      name: 'sendDate',
    })
    const selectionMetric = getFormValue({
      formState,
      name: 'selectionMetric',
    })?.value
    const splitCalculationType =
      optimizationSettingsConfiguration?.split_calculation?.type
    const listSize =
      splitCalculationType === 'fixed'
        ? optimizationSettingsConfiguration.split_calculation?.list_size
        : getFormValue({
            formState,
            name: 'listSize',
          })
    const openRate = getFormValue({
      formState,
      name: 'openRate',
    })
    const clickRate = getFormValue({
      formState,
      name: 'clickRate',
    })
    const splitNumber = getFormValue({
      formState,
      name: 'splitNumber',
    })

    const isISOFormat = moment(sendDate, moment.ISO_8601, true).isValid()
    const splitCalculationConfiguration =
      splitCalculationType === 'variant_dropdown' ||
      splitCalculationType === undefined
        ? { split_number: splitNumber, split_size: 0 }
        : splitCalculationResult

    const configuration: OptimizeMessageConfiguration = {
      ...optimizationSettingsConfiguration,
      ...(!flags.jacquardGeneratedControl && {
        own_subject_line: ownSubjectLine,
      }),
      campaign_name: campaignName,
      iso_send_date: isISOFormat
        ? sendDate
        : moment
            .parseZone(sendDate, REACT_APP_DEFAULT_DATE_FORMAT)
            .toISOString(),
      split_calculation: {
        ...optimizationSettingsConfiguration.split_calculation,
        ...(selectionMetric && { selection_metric: selectionMetric }),
        ...(splitNumber && { split_number: splitNumber }),
        ...(listSize && { list_size: listSize }),
        ...(openRate && { baseline_open_rate: openRate }),
        ...(clickRate && { baseline_click_rate: clickRate }),
        ...(splitCalculationResult && {
          configuration: splitCalculationConfiguration,
        }),
      },
    }

    return configuration
  }

  const onSave = () => {
    if (
      content &&
      selectedElement &&
      isComponentElement(selectedElement) &&
      selectedElement.optimization_settings
    ) {
      const configuration = getConfiguration()

      updateElementMutation.mutate({
        accountId,
        contentId: content?._id,
        elementId: selectedElement?.element_id,
        updatedElement: {
          optimization_settings: {
            template_id: selectedElement.optimization_settings.template_id,
            type: selectedElement.optimization_settings.type,
            configuration,
          },
        },
      })
    }
  }

  const onRegenerate = async () => {
    if (selectedElement && content) {
      const configuration = getConfiguration()
      const wsTopic = getWsTopic({
        data: {
          contentId: content._id,
          elementId: selectedElement!.element_id,
        },
        action: 'generateElementVariants',
      })

      sendWebsocketMessage({
        action: 'generateElementVariants',
        data: {
          contentId: content._id,
          elementId: selectedElement.element_id,
        },
        subscriptionAction: 'subscribe',
      })
      generateCustomVariantsMutation.mutate(
        {
          accountId,
          topics: selectedTopics,
          optimizationConfiguration: configuration,
          contentId: content._id,
          elementId: selectedElement?.element_id,
          wsTopic,
        },
        {
          onSuccess: () => {
            refetchContent()
            if (
              isComponentElement(selectedElement) &&
              !!selectedElement?.campaign_id
            ) {
              dispatch(fetchCampaignData(selectedElement?.campaign_id))
            }
          },
        }
      )
    }
  }

  const onResetClick = () => {
    form.setConfig('keepDirtyOnReinitialize', false)
    form.reset()
    form.setConfig('keepDirtyOnReinitialize', true)
    onReset()
  }

  const getState = () => {
    const dirtyFields = formState.dirtyFields
    const splitNumber = getFormValue({
      formState,
      name: 'splitNumber',
    })

    if (splitNumber !== initialSplitNumberRef.current) {
      return 'regenerate'
    } else if (!isEmpty(dirtyFields)) {
      return 'save'
    } else {
      return 'noChanges'
    }
  }

  const state = getState()

  return (
    <div className="mt-auto flex justify-end">
      {
        {
          regenerate: (
            <ActionsContainer>
              <Button
                variant="link"
                disabled={
                  isDisabled || generateCustomVariantsMutation.isLoading
                }
                onClick={onResetClick}
              >
                Revert
              </Button>
              <Button
                data-cy="content-regenerate-variants-button"
                data-testid="content-regenerate-variants-button"
                variant="primary"
                className="bg-yellow-400"
                disabled={
                  isDisabled ||
                  formState.pristine ||
                  formState.invalid ||
                  generateCustomVariantsMutation.isLoading
                }
                onClick={onRegenerate}
                loading={generateCustomVariantsMutation.isLoading}
              >
                Regenerate
              </Button>
            </ActionsContainer>
          ),
          save: (
            <ActionsContainer>
              <Button
                variant="link"
                disabled={isDisabled || updateElementMutation.isLoading}
                onClick={onResetClick}
              >
                Revert
              </Button>
              <Button
                data-cy="content-regenerate-variants-button"
                data-testid="content-regenerate-variants-button"
                variant="primary"
                className="bg-yellow-400"
                disabled={
                  isDisabled ||
                  formState.pristine ||
                  formState.invalid ||
                  updateElementMutation.isLoading
                }
                loading={updateElementMutation.isLoading}
                onClick={onSave}
              >
                Save
              </Button>
            </ActionsContainer>
          ),
          noChanges: null,
        }[state]
      }
    </div>
  )
}

export default ExperimentSettingsFooter
